/**
 * BookingV2Landing.js
 * ----------------------------------------------------------------------------
 * Standalone landing page for the V2 Booking Engine product module.
 *
 * Add a route for it in App.js, e.g.:
 *     <Route path="/booking-engine" element={<BookingV2Landing />} />
 *
 * Styles live in ../styles/BookingV2Landing.css (scoped to .bv2l).
 *
 * NOTE: sections use <div>, not <header>/<section>/<footer>, on purpose — the
 * app's global stylesheet has bare element rules (e.g. `header {}`) that were
 * painting a white card around the hero. Plain divs are immune to that.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/BookingV2Landing.css';

const SIGNUP_PATH = '/booking-engine/signup';

// A small, honest sample day. Drives the hero schedule — no real data needed.
const DAY = [
  { time: '09:00', label: 'Balayage + cut', state: 'booked' },
  { time: '10:30', label: 'Open', state: 'open' },
  { time: '11:15', label: 'Gel manicure', state: 'booked' },
  { time: '13:00', label: 'Lunch', state: 'blocked' },
  { time: '14:00', label: 'Colour consult', state: 'booked' },
  { time: '15:30', label: 'Open', state: 'open' },
];

function BookingV2Landing() {
  return (
    <div className="bv2l">
      <div className="wrap">
        <div className="bv2l-top">
          <div className="bv2l-mark">book<span>&middot;</span>engine</div>
          <Link className="ghost" to="/login">Sign in</Link>
        </div>

        <div className="bv2l-hero">
          <div className="bv2l-hero-copy">
            <div className="bv2l-eyebrow">Booking engine</div>
            <h1>Take bookings while your <em>hands are full</em>.</h1>
            <p className="bv2l-lede">
              A booking page that fills your calendar on its own &mdash; clients pick a
              real open slot, answer the questions you need, and you approve with a tap.
            </p>
            <div className="bv2l-cta">
              <Link className="bv2l-btn primary" to={SIGNUP_PATH}>Enable the booking engine</Link>
              <Link className="bv2l-btn text" to="/login">Already a vendor? Turn it on &rarr;</Link>
            </div>
          </div>

          {/* Signature: a live-feeling day, where the open slots are the point. */}
          <div className="bv2l-card" aria-label="Example day of bookings">
            <div className="bv2l-card-head">
              <b>Today</b>
              <small>2 slots open</small>
            </div>
            {DAY.map((s) => (
              <div key={s.time} className={`bv2l-slot ${s.state}`}>
                <span className="bv2l-time">{s.time}</span>
                <span className="bv2l-name">{s.label}</span>
                <span className="bv2l-tag">
                  {s.state === 'open' ? 'Bookable' : s.state === 'blocked' ? 'Held' : 'Booked'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bv2l-feat">
          <div className="item">
            <div className="num">01</div>
            <h3>Book with or without an account</h3>
            <p>New clients can book in seconds. Regulars get their history. Either way, the request lands in your inbox to approve.</p>
          </div>
          <div className="item">
            <div className="num">02</div>
            <h3>Ask the right questions</h3>
            <p>Build the intake form per service &mdash; hair length, allergies, first visit. The fields you choose, on your booking page.</p>
          </div>
          <div className="item">
            <div className="num">03</div>
            <h3>Never double-booked</h3>
            <p>Your hours, capacity, and blocked time decide what's offered, so the calendar only shows slots you can actually take.</p>
          </div>
        </div>

        <div className="bv2l-band">
          <h2>Switch it on and share one link.</h2>
          <Link className="bv2l-btn primary" to={SIGNUP_PATH}>Get started free</Link>
        </div>

        {/* <div className="bv2l-foot">
          Part of your loyalty dashboard &middot; <Link to="/login">Sign in</Link> to enable it on an existing account.
        </div> */}
      </div>
    </div>
  );
}

export default BookingV2Landing;